const JSPDF_CDN_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
const VOUCHER_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const VALIDITY_MONTHS = 6;

let jsPdfPromise = null;
const imageDataUriCache = new Map();

async function ensureJsPdf() {
  if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
  if (jsPdfPromise) return jsPdfPromise;

  jsPdfPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${JSPDF_CDN_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.jspdf?.jsPDF) resolve(window.jspdf.jsPDF);
        else reject(new Error('jsPDF failed to initialize'));
      });
      existing.addEventListener('error', () => reject(new Error('Unable to load jsPDF script')));
      return;
    }

    const script = document.createElement('script');
    script.src = JSPDF_CDN_URL;
    script.async = true;
    script.onload = () => {
      if (window.jspdf?.jsPDF) resolve(window.jspdf.jsPDF);
      else reject(new Error('jsPDF failed to initialize'));
    };
    script.onerror = () => reject(new Error('Unable to load jsPDF script'));
    document.head.appendChild(script);
  });

  return jsPdfPromise;
}

function toDate(value) {
  if (value instanceof Date) return value;
  if (!value) return new Date();
  if (typeof value === 'string' && /^\\d{4}-\\d{2}-\\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

export function addMonths(dateValue, months = VALIDITY_MONTHS) {
  const source = toDate(dateValue);
  const result = new Date(source.getTime());
  result.setMonth(result.getMonth() + months);
  return result;
}

export function formatDateFr(dateValue) {
  const value = toDate(dateValue);
  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const year = value.getFullYear();
  return `${day}/${month}/${year}`;
}

export function generateVoucherNumber(nowDate = new Date()) {
  const ym = `${nowDate.getFullYear()}${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
  let randomPart = '';
  for (let i = 0; i < 5; i += 1) {
    randomPart += VOUCHER_CHARS[Math.floor(Math.random() * VOUCHER_CHARS.length)];
  }
  return `MD-${ym}-${randomPart}`;
}

async function imageToDataUri(path) {
  if (imageDataUriCache.has(path)) return imageDataUriCache.get(path);
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Unable to load image: ${path}`);
  const blob = await response.blob();
  const dataUri = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  imageDataUriCache.set(path, dataUri);
  return dataUri;
}

function dataUriToBase64(dataUri) {
  const parts = String(dataUri).split(',');
  return parts.length > 1 ? parts[1] : parts[0];
}

export async function generateGiftVoucherPDF(options = {}) {
  const jsPDF = await ensureJsPdf();

  const clientName = (options.clientName || 'CLIENT 1').trim();
  const senderName = (options.senderName || 'CLIENT 2').trim();
  const amount = options.amount == null ? '**' : String(options.amount).trim();
  const message = (options.message || '').trim();
  const purchaseDate = options.purchaseDate || new Date().toISOString().split('T')[0];
  const voucherNumber = options.voucherNumber || generateVoucherNumber();
  const validUntil = options.validUntil || addMonths(purchaseDate, VALIDITY_MONTHS);
  const doDownload = options.download !== false;

  const validDateText = formatDateFr(validUntil);
  const bowData = dataUriToBase64(await imageToDataUri('/assets/gift-voucher/bow.png'));
  const heartData = dataUriToBase64(await imageToDataUri('/assets/gift-voucher/heart.png'));

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297;
  const H = 210;

  for (let i = 0; i < H; i += 1) {
    const t = i / H;
    doc.setFillColor(Math.round(253 - t * 5), Math.round(248 - t * 9), Math.round(244 - t * 12));
    doc.rect(0, i, W, 1.2, 'F');
  }

  doc.setDrawColor(201, 169, 110);
  doc.setLineWidth(0.75);
  doc.rect(7, 7, W - 14, H - 14);
  doc.setLineWidth(0.25);
  doc.rect(10.5, 10.5, W - 21, H - 21);

  const ry = H * 0.24;
  const rh = H * 0.25;
  [[245, 200, 215], [232, 160, 184], [218, 138, 164], [230, 158, 180], [242, 196, 212]].forEach((c, i) => {
    doc.setFillColor(c[0], c[1], c[2]);
    doc.rect(0, ry + i * rh / 5, W, rh / 5 + 0.5, 'F');
  });

  doc.setFillColor(255, 255, 255);
  doc.rect(0, ry, W, rh * 0.22, 'F');

  const bowImgW = W;
  const bowImgH = bowImgW * (993 / 1400);
  const bowImgY = ry + rh / 2 - bowImgH * 0.376;
  doc.addImage(bowData, 'PNG', 0, bowImgY, bowImgW, bowImgH);

  const hW = 52;
  const hH = hW * (126 / 147);
  const hY = H * 0.5 - hH / 2;
  doc.addImage(heartData, 'PNG', W * 0.018, hY, hW, hH);
  doc.addImage(heartData, 'PNG', W - W * 0.018 - hW, hY, hW, hH);

  doc.setFont('times', 'normal');
  doc.setTextColor(26, 26, 26);
  doc.setFontSize(58);
  doc.text('BON CADEAU', W / 2, H * 0.594, { align: 'center', charSpace: 2 });

  doc.setDrawColor(201, 169, 110);
  doc.setLineWidth(0.3);
  doc.line(W / 2 - 46, H * 0.617, W / 2 + 46, H * 0.617);

  const mY = H * 0.676;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(196, 104, 122);
  const pref = "D'UN MONTANT DE :   ";
  const pw11 = doc.getTextWidth(pref);
  doc.setFontSize(18);
  doc.setTextColor(26, 26, 26);
  const mntStr = `${amount}€`;
  const mw18 = doc.getTextWidth(mntStr);
  const startX = W / 2 - (pw11 + mw18) / 2;
  doc.setFontSize(11);
  doc.setTextColor(196, 104, 122);
  doc.text(pref, startX, mY);
  doc.setFontSize(18);
  doc.setTextColor(26, 26, 26);
  doc.text(mntStr, startX + pw11, mY);

  const nY = H * 0.757;
  const POUR = 'POUR   ';
  const DELA = '   ·   DE LA PART DE   ';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const pw11Names = doc.getTextWidth(POUR);
  doc.setFontSize(15);
  const c1w = doc.getTextWidth(clientName.toUpperCase());
  doc.setFontSize(11);
  const dw11Names = doc.getTextWidth(DELA);
  doc.setFontSize(15);
  const c2w = doc.getTextWidth(senderName.toUpperCase());
  const nLine = pw11Names * (15 / 11) + c1w + dw11Names * (15 / 11) + c2w;
  let nx = W / 2 - nLine / 2;
  doc.setFontSize(11);
  doc.setTextColor(196, 104, 122);
  doc.text(POUR, nx, nY);
  nx += pw11Names * (15 / 11);
  doc.setFontSize(15);
  doc.setTextColor(26, 26, 26);
  doc.text(clientName.toUpperCase(), nx, nY);
  nx += c1w;
  doc.setFontSize(11);
  doc.setTextColor(196, 104, 122);
  doc.text(DELA, nx, nY);
  nx += dw11Names * (15 / 11);
  doc.setFontSize(15);
  doc.setTextColor(26, 26, 26);
  doc.text(senderName.toUpperCase(), nx, nY);

  let validLineY = H * 0.872;
  if (message) {
    doc.setFont('times', 'italic');
    doc.setFontSize(12);
    doc.setTextColor(160, 140, 145);
    doc.text(message, W / 2, H * 0.828, { align: 'center', maxWidth: W - 80 });
    validLineY = H * 0.898;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(`VALABLE JUSQU'AU   ${validDateText}`, W / 2, validLineY, { align: 'center' });

  doc.setFont('courier', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(150, 118, 68);
  doc.text(`N° ${voucherNumber}`, W / 2, validLineY + 6.5, { align: 'center' });

  doc.setFont('times', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(198, 168, 108);
  doc.text('MD', W / 2, H - 11, { align: 'center' });

  const filename = `bon-cadeau-${voucherNumber}.pdf`;
  if (doDownload) {
    doc.save(filename);
    return null;
  }
  return doc.output('blob');
}

export const giftVoucherPdfConfig = {
  validityMonths: VALIDITY_MONTHS,
  voucherChars: VOUCHER_CHARS,
  jsPdfCdnUrl: JSPDF_CDN_URL
};
